import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '~/lib/auth-context'
import { trpc } from '~/utils/trpc'
import { useReactivePathname } from '~/lib/router-location-context'
import {
  isAdminTermsRoute,
  isPublicTermsRoute,
  readTermsVersionParam,
  stripTermsVersionParam,
} from '~/lib/terms-acceptance'
import { TermsAcceptanceScreen } from './TermsAcceptanceScreen'

/**
 * B-new-900 — gates every authenticated route behind terms/privacy
 * acceptance. Lives in the same place `AuthProvider` renders from
 * (`apps/web/src/lib/auth-context.tsx` wraps `<TermsAcceptanceGate>` around
 * its children) — one place, per the roadmap row.
 *
 * NEVER blocks:
 *  - public routes (sign-in/up, oauth/callback, verify-email,
 *    email-preferences, accessibility-statement) — `isPublicTermsRoute`.
 *  - `/admin/*` for an ADMIN_EMAIL-allowlisted operator — checked
 *    client-side the same way Header.tsx / AdminGuard.tsx do
 *    (`user.email === VITE_ADMIN_EMAIL`), so an admin account that itself
 *    needs acceptance still isn't locked out of the admin console. It DOES
 *    still see the screen on non-admin routes.
 *  - a signed-out visitor, or while the session itself is still resolving
 *    (`isLoading` from useAuth) — nothing to gate yet either way.
 *
 * Magic-link sign-ups carry `termsV=<version>` on the callback URL
 * (`buildMagicLinkCallbackUrl`, sign-up.lazy.tsx) and never see this screen:
 * on first authenticated landing this auto-accepts, once, clearing the URL
 * param only on confirmed success (same only-clear-on-success shape as
 * B-new-870's lifecycle opt-out) — a failed auto-accept still strips the
 * param (to avoid retrying a possibly-stale version forever) and falls
 * through to the normal screen, which always re-fetches the current
 * version, so the user is never silently blocked or looped.
 */
export function TermsAcceptanceGate({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const { user, isLoading: authLoading } = useAuth()
  const utils = trpc.useUtils()

  // B-new-1010 — reactive: a plain `window.location.pathname` read here
  // never re-renders on client-side navigation (`AuthProvider`, and
  // therefore this gate, sits above `RouterProvider` in main.tsx — see
  // router-location-context.tsx). Falls back to the same one-shot
  // `window.location.pathname` read when no `RouterLocationProvider` is
  // mounted (every existing test that renders this gate bare), so the
  // fallback path is unchanged.
  const pathname = useReactivePathname()
  const isPublic = isPublicTermsRoute(pathname)
  const isAdminRoute = isAdminTermsRoute(pathname)
  const adminEmail = import.meta.env.VITE_ADMIN_EMAIL
  const isAdminUser = !!adminEmail && user?.email === adminEmail

  const termsVParam = readTermsVersionParam(window.location.search)
  const [autoAcceptPending, setAutoAcceptPending] = useState(!!termsVParam)
  const autoAcceptAppliedRef = useRef(false)

  const acceptTermsMutation = trpc.user.acceptTerms.useMutation()
  const termsStatusQuery = trpc.user.getTermsStatus.useQuery(undefined, {
    enabled: !!user && !authLoading && !isPublic,
  })

  // Magic-link auto-accept (B-new-900 #3) — runs once per authenticated
  // landing that carries `termsV`.
  useEffect(() => {
    if (authLoading || !user) return
    if (!termsVParam) return
    if (autoAcceptAppliedRef.current) return
    autoAcceptAppliedRef.current = true

    acceptTermsMutation.mutate(
      // eu-compliance-reviewer C2 blocker — the server only honours
      // `source: 'magic_link_signup'` for an account with no prior
      // acceptance created in the last 30 minutes; anything else (a
      // crafted/shared/stale `?termsV=` link, or a real but old link)
      // is rejected, and the onError branch below falls through to the
      // normal screen instead of silently recording consent.
      { version: termsVParam, source: 'magic_link_signup' },
      {
        onSuccess: () => {
          setAutoAcceptPending(false)
          utils.user.getTermsStatus.invalidate()
          const clean = stripTermsVersionParam(window.location.pathname, window.location.search)
          window.history.replaceState({}, '', clean)
        },
        onError: () => {
          // Stale version (rejected) or a transient failure — either way,
          // strip the param so this doesn't retry against a version that
          // may no longer be current, and fall through to the normal
          // screen below (which fetches the CURRENT version fresh, so the
          // user can always accept manually — never a silent lockout).
          const clean = stripTermsVersionParam(window.location.pathname, window.location.search)
          window.history.replaceState({}, '', clean)
          setAutoAcceptPending(false)
        },
      },
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.id])

  // Nothing to gate: public route, signed-out, session still resolving, or
  // an admin account on an admin route.
  if (isPublic) return <>{children}</>
  if (authLoading || !user) return <>{children}</>
  if (isAdminRoute && isAdminUser) return <>{children}</>

  // eu-compliance-reviewer fix — this used to `return children` here (and
  // for the loading branch below), i.e. render full app content BEFORE
  // knowing whether the account needs acceptance. That's a fail-open flash:
  // for a split second (or the whole in-flight auto-accept window) an
  // account that needs the screen sees the real app instead. Neither
  // in-flight state is public/signed-out/admin-exempt, so neither may ever
  // render `children` — a neutral loader is the only safe placeholder.
  // Error first, so an errored query still gets retry/sign-out.
  if (termsStatusQuery.isError) {
    return (
      <TermsStatusErrorScreen
        onRetry={() => termsStatusQuery.refetch()}
      />
    )
  }

  // B-new-1010 (eu-compliance-reviewer): `!data` also covers a PAUSED query
  // (offline: isLoading false, isError false, no data), which used to fall
  // through to `children` — the same fail-open this block exists to prevent.
  if (autoAcceptPending || termsStatusQuery.isLoading || !termsStatusQuery.data) {
    return <TermsStatusLoadingScreen />
  }

  if (termsStatusQuery.data?.needsAcceptance) {
    const locale = pathname.split('/')[1] || 'en'
    const marketingUrl = import.meta.env.VITE_MARKETING_URL || 'https://xeito.ai'
    return (
      <TermsAcceptanceScreen
        currentVersion={termsStatusQuery.data.currentVersion}
        locale={locale}
        marketingUrl={marketingUrl}
        isSubmitting={acceptTermsMutation.isPending}
        submitError={acceptTermsMutation.isError}
        onAccept={({ lifecycleEmailOptOut }) => {
          acceptTermsMutation.mutate(
            { version: termsStatusQuery.data.currentVersion, lifecycleEmailOptOut, source: 'screen' },
            {
              onSuccess: () => {
                utils.user.getTermsStatus.invalidate()
              },
              // Deliberately no onError beyond the mutation's own isError
              // state, surfaced by TermsAcceptanceScreen — the screen stays
              // up (never let a failed save through) and the button
              // re-enables (isSubmitting flips back to false) so the user
              // can retry immediately.
            },
          )
        }}
      />
    )
  }

  return <>{children}</>
}

/** Neutral full-page loader — never the real app, never the acceptance
 *  screen — shown while `getTermsStatus` is in flight or a magic-link
 *  auto-accept is pending. No copy needed beyond an accessible label. */
function TermsStatusLoadingScreen() {
  const { t } = useTranslation()
  return (
    <main
      id="main-content"
      role="main"
      aria-busy="true"
      data-testid="terms-status-loading-screen"
      className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4 py-12"
    >
      <span className="sr-only">{t('common.loading', 'Loading...')}</span>
      <div className="w-8 h-8 border-2 border-[#00f0ff]/30 border-t-[#00f0ff] rounded-full animate-spin" aria-hidden="true" />
    </main>
  )
}

function TermsStatusErrorScreen({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation()
  const { signOut } = useAuth()
  return (
    <main
      id="main-content"
      role="main"
      data-testid="terms-status-error-screen"
      className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-md border-2 border-[#ff00ff]/30 bg-[#0f0f15]/95 p-8 rounded-lg text-center">
        <p role="alert" className="text-sm font-mono text-[#ff00ff] mb-6">
          {t('auth.termsAcceptance.statusLoadError', "Couldn't load your account status. Try again.")}
        </p>
        <button
          type="button"
          onClick={onRetry}
          className="w-full py-3 bg-[#00f0ff] hover:bg-[#39ff14] text-[#0a0a0f] font-mono font-bold text-sm rounded-lg mb-4"
        >
          {t('auth.termsAcceptance.retry', 'Retry')}
        </button>
        <button
          type="button"
          onClick={() => signOut()}
          className="w-full text-center text-xs font-mono text-[#a0a0b0] underline hover:text-white"
        >
          {t('auth.signOutLink', 'Sign out')}
        </button>
      </div>
    </main>
  )
}
